<template>
  <div>
    <div class="loading-state-container" v-if="loadEpisodeState && loadEpisodeState.status === 'loading'">
      <div class="container">
        <PreloaderSection />
      </div>
    </div>
    <Page v-if="loadEpisodeState && loadEpisodeState.status === 'success'">
      <ConfirmModal
        v-if="modal"
        :onClose="handleModal"
        :onConfirm="onDelete">
        <template v-slot:header-title>
          <h4>ยืนยันการลบ</h4>
        </template>
        <div>คุณยืนยันที่จะลบตอน {{getEpisodeName()}}</div>
      </ConfirmModal>
      <template v-slot:page-header>
        <div class="container">
          <div class="page-header">
            <router-link
              class="btn btn-action btn-action-back"
              :to="`/episodes`">
              <i class="fas fa-chevron-left" />
            </router-link>
            <div class="title-container">
              <div class="subtitle">แก้ไขตอน</div>
              <div class="title">{{oldEpisodeTitle}}</div>
            </div>
            <div class="actions">
              <small class="text-danger mr-3"
                v-if="getServerError()">
                {{getServerError()}}
              </small>
              <small class="text-success font-weight-bold mr-3" v-if="saveEpisodeState.status === 'saved'">
                <i class="fa fa-check" /> บันทึกแล้ว
              </small>
              <span v-if="isSaving">
                <i class="fa fa-spin fa-spinner mr-1" />
                <small class="mr-2">
                  กำลังบันทึก
                </small>
              </span>
              <button
                type="submit"
                class="btn btn-primary"
                :disabled="disableButton"
                @click.stop="onSubmit">
                บันทึก
              </button>
              <Dropdown :alignment="'right'">
                <template v-slot:dropdow-button>
                  <button
                    type="button"
                    class="btn btn-outline-dark btn-action m-0 ml-2">
                    <i class="fas fa-cog"></i>
                  </button>
                </template>
                <a :href="getPreviewLink()" target="_blank" class="dropdown-item">ดูตัวอย่าง</a>
                <div class="dropdown-item" @click.stop="handleModal">
                  ลบตอน
                </div>
              </Dropdown>
            </div>
          </div>
        </div>
      </template>
      <div class="container">
        <Tab>
          <div
            class="tab-item"
            v-for="(tabItem, tabIndex) in tabMenu"
            :key="tabIndex"
            :class="{'active': selectedTabIndex === tabIndex }"
            @click.stop="selectedTab(tabIndex)">
            {{tabItem.value}}
          </div>
        </Tab>
        <div
          class="tab-item tab-item-general"
          v-if="selectedTabIndex === 0">
          <div class="section-container">
            <SectionEpisodeInformation />
          </div>
          <div class="section-container">
            <SectionGallery />
          </div>
          <div class="section-container">
            <SectionAttachment />
          </div>
        </div>
        <div class="tab-item tab-item-general" v-if="selectedTabIndex === 1">
          <div class="section-container">
            <SectionContentImage
              @update:image="(payload) => $store.dispatch('setCurrentEpisode', payload)"
              :item="currentEpisode" />
          </div>
        </div>
      </div>
    </Page>
    <ErrorMessageEpisodeContainer
      v-if="loadEpisodeState && loadEpisodeState.status === 'error'"
      :error="loadEpisodeState.error"
      :redirectLink="'/episodes'" />
  </div>
</template>

<script>
import _ from 'lodash';
import { mapState } from 'vuex';

import SectionAttachment from '@/attachment/SectionAttachment.vue';

import dateFormetter from '@/utils/dateFormatter';
import errorFormatter from '@/utils/errorFormatter';

import Page from '@/layouts/Page.vue';
import Dropdown from '@/components/Dropdown.vue';
import ConfirmModal from '@/components/ConfirmModal.vue';
import Tab from '@/components/Tab.vue';
import SectionContentImage from '@/image/SectionContentImage.vue';
import SectionGallery from '@/gallery/SectionGallery.vue';

import PreloaderSection from '@/components/PreloaderSection.vue';
import ErrorMessageEpisodeContainer from './ErrorMessageEpisodeContainer.vue';
import SectionEpisodeInformation from './SectionEpisodeInformation.vue';

export default {
  mixins: [dateFormetter, errorFormatter],
  components: {
    Page,
    SectionEpisodeInformation,
    SectionGallery,
    SectionAttachment,
    Dropdown,
    ConfirmModal,
    SectionContentImage,
    Tab,
    PreloaderSection,
    ErrorMessageEpisodeContainer,
  },
  data() {
    return {
      tabMenu: [
        {
          key: 'information',
          value: 'รายละเอียด',
        },
        {
          key: 'contentImage',
          value: 'รูปปก',
        },
      ],
      selectedTabIndex: 0,
      gallery: null,
      modal: false,
      oldEpisodeTitle: '',
    };
  },
  created() {
    this.load();
  },
  computed: {
    ...mapState({
      currentEpisode: (state) => state.episodeItem.currentEpisode,
      loadEpisodeState: (state) => state.episodeItem.loadEpisodeState,
      saveEpisodeState: (state) => state.episodeItem.saveEpisodeState,
      attachment: (state) => state.attachment.attachment,
      currentGallery: (state) => state.gallery.currentGallery,
    }),
    episodeId() {
      return this.$route.params.episodeId;
    },
    isEpisodeValid() {
      return this.currentEpisode.title && this.currentEpisode.programId;
    },
    isProgramValid() {
      return this.currentEpisode.programId;
    },
    haveErrorMessage() {
      return _.some(this.saveEpisodeState.error, (value) => value);
    },
    isSaving() {
      return this.saveEpisodeState.status === 'saving';
    },
    disableButton() {
      return this.haveErrorMessage || this.isSaving;
    },
    isContentCategoryValid() {
      if (this.episodeStatus !== 'draft') {
        return !!this.episodeContentCategory;
      }
      return true;
    },
  },
  methods: {
    async load() {
      await this.$store.dispatch('loadEpisodeById', this.episodeId);
      this.oldEpisodeTitle = this.getEpisodeName();
    },
    async onSubmit() {
      this.currentEpisode.galleryId = this.currentGallery.id;
      await this.$store.dispatch('updateEpisodeById', { episodeId: this.episodeId, payload: this.currentEpisode });
      this.oldEpisodeTitle = this.getEpisodeName();
    },
    async onDelete() {
      await this.$store.dispatch('deleteEpisode', this.episodeId);
      this.$router.push('/episodes');
    },
    handleModal() {
      this.modal = !this.modal;
    },
    selectedTab(selectedTabIndex) {
      this.selectedTabIndex = selectedTabIndex;
    },
    getServerError() {
      return _.get(this.saveEpisodeState, 'error.serverError', null);
    },
    getEpisodeName() {
      return _.get(this.currentEpisode, 'title', this.episodeId);
    },
    getPreviewLink() {
      return _.get(this.currentEpisode, 'previewUrl', null);
    },
  },
};
</script>

<style lang="scss" scoped>
@import './src/assets/scss/variables.scss';
@import './src/assets/scss/themes/layouts/page.scss';
@import './src/assets/scss/themes/components/section-container.scss';
</style>
